import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  FiTag,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiX,
  FiEdit2,
  FiToggleLeft,
  FiToggleRight,
  FiCheckCircle,
  FiAlertCircle,
  FiChevronLeft,
  FiChevronRight,
  FiRefreshCw,
  FiEye,
  FiPercent,
  FiUsers,
  FiCalendar,
  FiZap,
} from "react-icons/fi";
import {
  createOffer,
  getAllPostOffers,
  updateOffer,
  toggleOfferStatus,
  deleteOffer,
} from "../api/offerApi";
import { BsCurrencyRupee } from "react-icons/bs";

const LIMIT = 10;

const DISCOUNT_TYPES = [
  { value: "percentage", label: "Percentage (%)" },
  { value: "flat", label: "Flat Amount (₹)" },
];

const VEHICLE_TYPES = ["auto", "cab", "bike", "suv"];

// ── Status + color maps ───────────────────────────────────────────────────
const ACTIVE_COLORS = {
  true:  { bg: "#E8F5E9", color: "#2E7D32", label: "Active" },
  false: { bg: "#FFEBEE", color: "#C62828", label: "Inactive" },
};

const DISCOUNT_COLORS = {
  percentage: { bg: "#EEF2FF", color: "#4338CA" },
  flat:       { bg: "#FFF7ED", color: "#C2410C" },
};

// ── Inline Styles (exact same pattern as NotificationPage) ────────────────
const S = {
  page: {
    padding: "28px 32px",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    background: "#F7F8FA",
    minHeight: "100vh",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  title: { fontSize: 22, fontWeight: 700, color: "#111827", display: "flex", alignItems: "center", gap: 10 },
  subtitle: { fontSize: 13, color: "#6B7280", marginTop: 3 },

  statsRow: { display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" },
  statCard: (color) => ({
    background: "#fff",
    border: `1.5px solid ${color}22`,
    borderRadius: 12,
    padding: "14px 20px",
    flex: "1 1 120px",
    minWidth: 120,
  }),
  statNum: (color) => ({ fontSize: 26, fontWeight: 800, color }),
  statLabel: { fontSize: 12, color: "#9CA3AF", marginTop: 2 },

  card: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #E5E7EB",
    padding: "20px 24px",
    marginBottom: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  cardTitle: { fontSize: 14, fontWeight: 700, color: "#374151", marginBottom: 16, display: "flex", alignItems: "center", gap: 7 },

  row: { display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 5, flex: "1 1 160px", minWidth: 140 },
  label: { fontSize: 12, fontWeight: 600, color: "#374151" },
  input: {
    border: "1px solid #D1D5DB",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    outline: "none",
    transition: "border .15s",
    background: "#fff",
  },
  select: {
    border: "1px solid #D1D5DB",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    outline: "none",
    background: "#fff",
    cursor: "pointer",
  },
  textarea: {
    border: "1px solid #D1D5DB",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    outline: "none",
    resize: "vertical",
    fontFamily: "inherit",
    transition: "border .15s",
  },
  btnPrimary: {
    background: "#1d9e75",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "9px 18px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  btnDanger: {
    background: "#FEE2E2",
    color: "#DC2626",
    border: "none",
    borderRadius: 6,
    padding: "5px 8px",
    fontSize: 12,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
  },
  btnGhost: {
    background: "transparent",
    color: "#6B7280",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    padding: "8px 14px",
    fontSize: 13,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  btnIcon: {
    background: "transparent",
    border: "none",
    borderRadius: 6,
    padding: "5px 7px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    color: "#6B7280",
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: "1px solid #D1D5DB",
    borderRadius: 8,
    padding: "8px 12px",
    background: "#fff",
    flex: "1 1 200px",
  },
  searchInput: {
    border: "none",
    outline: "none",
    fontSize: 13,
    flex: 1,
    background: "transparent",
  },
  filterTab: (active) => ({
    padding: "6px 14px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    border: active ? "none" : "1px solid #E5E7EB",
    background: active ? "#1d9e75" : "#fff",
    color: active ? "#fff" : "#6B7280",
    transition: "all .15s",
  }),
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    fontSize: 11,
    fontWeight: 700,
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: ".04em",
    padding: "10px 12px",
    borderBottom: "1px solid #F3F4F6",
    background: "#F9FAFB",
  },
  td: { padding: "11px 12px", fontSize: 13, color: "#374151", borderBottom: "1px solid #F3F4F6", verticalAlign: "middle" },
  badge: (active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
    background: (ACTIVE_COLORS[active] || ACTIVE_COLORS.false).bg,
    color: (ACTIVE_COLORS[active] || ACTIVE_COLORS.false).color,
  }),
  discountBadge: (type) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 9px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    background: (DISCOUNT_COLORS[type] || DISCOUNT_COLORS.flat).bg,
    color: (DISCOUNT_COLORS[type] || DISCOUNT_COLORS.flat).color,
  }),
  pagination: { display: "flex", alignItems: "center", gap: 8, justifyContent: "center", marginTop: 20 },
  pageBtn: (active) => ({
    width: 34,
    height: 34,
    borderRadius: 8,
    border: active ? "none" : "1px solid #E5E7EB",
    background: active ? "#1d9e75" : "#fff",
    color: active ? "#fff" : "#374151",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  toast: (type) => ({
    position: "fixed",
    top: 24,
    right: 24,
    zIndex: 9999,
    background: type === "success" ? "#1d9e75" : "#DC2626",
    color: "#fff",
    padding: "12px 20px",
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 8,
    boxShadow: "0 4px 20px rgba(0,0,0,.15)",
    animation: "slideIn .25s ease",
  }),
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#9CA3AF",
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    zIndex: 1000,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    overflowY: "auto",
    padding: "40px 16px",
  },
  modal: {
    background: "#fff",
    borderRadius: 16,
    width: "100%",
    maxWidth: 620,
    padding: "28px 28px 24px",
    boxShadow: "0 8px 40px rgba(0,0,0,.18)",
    position: "relative",
  },
  modalTitle: { fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 20, display: "flex", alignItems: "center", gap: 8 },
  divider: { height: 1, background: "#F3F4F6", margin: "16px 0" },
  hint: { fontSize: 11, color: "#9CA3AF", marginTop: 3 },
};

// ── Helpers ───────────────────────────────────────────────────────────────
const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

const isExpired = (validUntil) => validUntil && new Date(validUntil) < new Date();

const defaultForm = {
  code: "",
  title: "",
  description: "",
  discountType: "percentage",
  discountValue: "",
  maxDiscountAmount: "",
  minFareAmount: "",
  validFrom: new Date().toISOString().split("T")[0],
  validUntil: "",
  totalUsageLimit: "",
  perPassengerLimit: "1",
  targetPassengerIds: "",
  applicableVehicleTypes: [],
  isActive: true,
};

// ─────────────────────────────────────────────────────────────────────────────
export default function OfferPage() {

  // ── List State ────────────────────────────────────────────────────────────
  const [offers, setOffers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, expired: 0, totalUsed: 0 });
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalCount: 0, hasNextPage: false, hasPrevPage: false });
  const [loading, setLoading] = useState(true);

  // ── Filter State ──────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm]         = useState("");
  const [filterDiscountType, setFilterDiscountType] = useState("");
  const [filterActive, setFilterActive]     = useState("");
  const [filterValidOnly, setFilterValidOnly] = useState(false);
  const [currentPage, setCurrentPage]       = useState(1);
  const debounceRef = useRef(null);

  // ── Form State ────────────────────────────────────────────────────────────
  const [showForm, setShowForm]     = useState(false);
  const [editMode, setEditMode]     = useState(false);   // false = create, true = edit
  const [editId, setEditId]         = useState(null);
  const [form, setForm]             = useState(defaultForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // ── Preview Modal ─────────────────────────────────────────────────────────
  const [previewOffer, setPreviewOffer] = useState(null);

  // ── Delete Confirm ────────────────────────────────────────────────────────
  const [deleteConfirm, setDeleteConfirm] = useState(null); // offer object

  // ── Toast ─────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "success" }), 3000);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  FETCH
  // ─────────────────────────────────────────────────────────────────────────
  const fetchOffers = useCallback(async (page, search, discountType, isActive, validOnly) => {
    try {
      setLoading(true);
      const payload = {
        n_page: page,
        n_limit: LIMIT,
        c_search_term: (search || "").trim(),
        c_discount_type: discountType || "",
        c_is_active: isActive !== undefined && isActive !== "" ? String(isActive) : "",
        c_valid_only: validOnly ? "true" : "",
      };
      const res = await getAllPostOffers(payload);
      if (!res.success) { setOffers([]); return; }
      setOffers(res.offers || []);
      setPagination(res.pagination || { currentPage: 1, totalPages: 1, totalCount: 0, hasNextPage: false, hasPrevPage: false });
      if (res.stats) setStats(res.stats);
    } catch (err) {
      console.error(err);
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOffers(1, "", "", "", false); }, [fetchOffers]);

  // ── Debounced search ──────────────────────────────────────────────────────
  const handleSearch = (e) => {
    const v = e.target.value;
    setSearchTerm(v);
    setCurrentPage(1);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchOffers(1, v, filterDiscountType, filterActive, filterValidOnly), 400);
  };

  const applyFilter = (key, val) => {
    const next = {
      discountType: filterDiscountType,
      isActive: filterActive,
      validOnly: filterValidOnly,
      [key]: val,
    };
    setFilterDiscountType(next.discountType);
    setFilterActive(next.isActive);
    setFilterValidOnly(next.validOnly);
    setCurrentPage(1);
    fetchOffers(1, searchTerm, next.discountType, next.isActive, next.validOnly);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.totalPages) return;
    setCurrentPage(page);
    fetchOffers(page, searchTerm, filterDiscountType, filterActive, filterValidOnly);
    window.scrollTo({ top: 300, behavior: "smooth" });
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  FORM VALIDATION
  // ─────────────────────────────────────────────────────────────────────────
  const validateForm = () => {
    const e = {};
    if (!form.code.trim())         e.code         = "Offer code required";
    if (!form.title.trim())        e.title        = "Title required";
    if (!form.discountValue)       e.discountValue = "Discount value required";
    if (form.discountType === "percentage") {
      if (Number(form.discountValue) <= 0 || Number(form.discountValue) > 100)
        e.discountValue = "Must be between 1 and 100";
    } else {
      if (Number(form.discountValue) <= 0) e.discountValue = "Must be greater than 0";
    }
    if (!form.validUntil) e.validUntil = "Expiry date required";
    setFormErrors(e);
    return Object.keys(e).length === 0;
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  CREATE / UPDATE
  // ─────────────────────────────────────────────────────────────────────────
  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const payload = {
        code: form.code.toUpperCase().trim(),
        title: form.title.trim(),
        description: form.description.trim(),
        discountType: form.discountType,
        discountValue: Number(form.discountValue),
        maxDiscountAmount: form.maxDiscountAmount ? Number(form.maxDiscountAmount) : null,
        minFareAmount: form.minFareAmount ? Number(form.minFareAmount) : 0,
        validFrom: form.validFrom || new Date().toISOString(),
        validUntil: new Date(form.validUntil).toISOString(),
        totalUsageLimit: form.totalUsageLimit ? Number(form.totalUsageLimit) : null,
        perPassengerLimit: Number(form.perPassengerLimit) || 1,
        targetPassengerIds: form.targetPassengerIds
          ? form.targetPassengerIds.split(",").map(s => s.trim()).filter(Boolean)
          : [],
        applicableVehicleTypes: form.applicableVehicleTypes || [],
        isActive: form.isActive,
      };

      let res;
      if (editMode && editId) {
        res = await updateOffer(editId, payload);
      } else {
        res = await createOffer(payload);
      }

      if (res.success) {
        showToast(editMode ? "Offer updated successfully!" : "Offer created successfully!");
        setShowForm(false);
        setEditMode(false);
        setEditId(null);
        setForm(defaultForm);
        setFormErrors({});
        fetchOffers(currentPage, searchTerm, filterDiscountType, filterActive, filterValidOnly);
      } else {
        showToast(res.error || "Failed to save offer", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  TOGGLE
  // ─────────────────────────────────────────────────────────────────────────
  const handleToggle = async (offer) => {
    const res = await toggleOfferStatus(offer.offerId || offer._id);
    if (res.success) {
      showToast(`Offer ${res.isActive ? "activated" : "deactivated"}!`);
      fetchOffers(currentPage, searchTerm, filterDiscountType, filterActive, filterValidOnly);
    } else {
      showToast(res.error || "Toggle failed", "error");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  DELETE
  // ─────────────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    const res = await deleteOffer(deleteConfirm.offerId || deleteConfirm._id);
    if (res.success) {
      showToast("Offer deleted!");
      setDeleteConfirm(null);
      fetchOffers(currentPage, searchTerm, filterDiscountType, filterActive, filterValidOnly);
    } else {
      showToast(res.error || "Delete failed", "error");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  EDIT → prefill form
  // ─────────────────────────────────────────────────────────────────────────
  const openEdit = (offer) => {
    setForm({
      code: offer.code || "",
      title: offer.title || "",
      description: offer.description || "",
      discountType: offer.discountType || "percentage",
      discountValue: String(offer.discountValue || ""),
      maxDiscountAmount: offer.maxDiscountAmount != null ? String(offer.maxDiscountAmount) : "",
      minFareAmount: offer.minFareAmount != null ? String(offer.minFareAmount) : "",
      validFrom: offer.validFrom ? offer.validFrom.split("T")[0] : new Date().toISOString().split("T")[0],
      validUntil: offer.validUntil ? offer.validUntil.split("T")[0] : "",
      totalUsageLimit: offer.totalUsageLimit != null ? String(offer.totalUsageLimit) : "",
      perPassengerLimit: String(offer.perPassengerLimit || 1),
      targetPassengerIds: Array.isArray(offer.targetPassengerIds) ? offer.targetPassengerIds.join(", ") : "",
      applicableVehicleTypes: offer.applicableVehicleTypes || [],
      isActive: offer.isActive !== undefined ? offer.isActive : true,
    });
    setEditMode(true);
    setEditId(offer.offerId || offer._id);
    setFormErrors({});
    setShowForm(true);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  VEHICLE TYPE TOGGLE HELPER
  // ─────────────────────────────────────────────────────────────────────────
  const toggleVehicle = (v) => {
    const list = form.applicableVehicleTypes;
    setForm({
      ...form,
      applicableVehicleTypes: list.includes(v) ? list.filter(x => x !== v) : [...list, v],
    });
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  PAGINATION RANGE
  // ─────────────────────────────────────────────────────────────────────────
  const pageNumbers = () => {
    const total = pagination.totalPages;
    const cur   = currentPage;
    if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
    if (cur <= 3) return [1, 2, 3, 4, "...", total];
    if (cur >= total - 2) return [1, "...", total - 3, total - 2, total - 1, total];
    return [1, "...", cur - 1, cur, cur + 1, "...", total];
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <style>{`
        @keyframes slideIn { from { transform: translateX(40px); opacity: 0 } to { transform: none; opacity: 1 } }
        .offer-input:focus { border-color: #1d9e75 !important; }
        .offer-btn-primary:hover { background: #178a63 !important; }
        .offer-btn-ghost:hover { background: #F9FAFB !important; }
        .offer-row:hover td { background: #FAFAFA; }
      `}</style>

      {/* ── Toast ── */}
      {toast.show && (
        <div style={S.toast(toast.type)}>
          {toast.type === "success" ? <FiCheckCircle size={16} /> : <FiAlertCircle size={16} />}
          {toast.message}
        </div>
      )}

      {/* ── Header ── */}
      <div style={S.header}>
        <div>
          <div style={S.title}><FiTag size={22} color="#1d9e75" /> Offers & Promo Codes</div>
          <div style={S.subtitle}>Create and manage discount offers for passengers</div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button style={S.btnGhost} className="offer-btn-ghost"
            onClick={() => fetchOffers(currentPage, searchTerm, filterDiscountType, filterActive, filterValidOnly)}>
            <FiRefreshCw size={14} />
          </button>
          <button style={S.btnPrimary} className="offer-btn-primary"
            onClick={() => { setForm(defaultForm); setEditMode(false); setEditId(null); setFormErrors({}); setShowForm(true); }}>
            <FiPlus size={15} /> Create Offer
          </button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div style={S.statsRow}>
        {[
          { label: "Total Offers",  value: stats.total,     color: "#1d9e75" },
          { label: "Active",        value: stats.active,    color: "#2563EB" },
          { label: "Expired",       value: stats.expired,   color: "#DC2626" },
          { label: "Total Used",    value: stats.totalUsed, color: "#7C3AED" },
        ].map(s => (
          <div key={s.label} style={S.statCard(s.color)}>
            <div style={S.statNum(s.color)}>{s.value}</div>
            <div style={S.statLabel}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div style={S.card}>
        <div style={{ ...S.row, marginBottom: 14 }}>
          {/* Search */}
          <div style={S.searchBox}>
            <FiSearch size={15} color="#9CA3AF" />
            <input
              style={S.searchInput}
              placeholder="Search code, title…"
              value={searchTerm}
              onChange={handleSearch}
            />
            {searchTerm && (
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", display: "flex" }}
                onClick={() => { setSearchTerm(""); fetchOffers(1, "", filterDiscountType, filterActive, filterValidOnly); }}>
                <FiX size={14} />
              </button>
            )}
          </div>

          {/* Discount type filter */}
          <select style={{ ...S.select, flex: "0 1 160px" }}
            value={filterDiscountType}
            onChange={e => applyFilter("discountType", e.target.value)}>
            <option value="">All Discount Types</option>
            {DISCOUNT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        {/* Tab filters */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[
            { key: "", label: "All" },
            { key: "true", label: "Active" },
            { key: "false", label: "Inactive" },
          ].map(tab => (
            <button key={tab.key} style={S.filterTab(filterActive === tab.key)} className="offer-filter-tab"
              onClick={() => applyFilter("isActive", tab.key)}>
              {tab.label}
            </button>
          ))}
          <button
            style={S.filterTab(filterValidOnly)}
            onClick={() => applyFilter("validOnly", !filterValidOnly)}>
            {filterValidOnly ? "✓ " : ""}Valid Now
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={S.cardTitle}><FiTag size={15} /> Offers ({pagination.totalCount})</div>
        </div>

        {loading ? (
          <div style={S.emptyState}>
            <FiRefreshCw size={28} style={{ animation: "spin 1s linear infinite" }} />
            <div style={{ marginTop: 10 }}>Loading offers…</div>
          </div>
        ) : offers.length === 0 ? (
          <div style={S.emptyState}>
            <FiTag size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
            <div>No offers found</div>
            <div style={{ fontSize: 12, marginTop: 6 }}>Create your first offer to get started</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr>
                  {["Code", "Title", "Discount", "Min Fare", "Valid Until", "Usage", "Status", "Actions"].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {offers.map(offer => {
                  const expired = isExpired(offer.validUntil);
                  return (
                    <tr key={offer._id} className="offer-row">
                      {/* Code */}
                      <td style={S.td}>
                        <span style={{
                          fontFamily: "monospace",
                          fontWeight: 700,
                          fontSize: 13,
                          background: "#F0FDF4",
                          color: "#166534",
                          padding: "3px 8px",
                          borderRadius: 6,
                          letterSpacing: 1,
                        }}>
                          {offer.code}
                        </span>
                      </td>

                      {/* Title */}
                      <td style={S.td}>
                        <div style={{ fontWeight: 600, color: "#111827", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {offer.title}
                        </div>
                        {offer.description && (
                          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {offer.description}
                          </div>
                        )}
                      </td>

                      {/* Discount */}
                      <td style={S.td}>
                        <span style={S.discountBadge(offer.discountType)}>
                          {offer.discountType === "percentage" ? <FiPercent size={10} /> : <BsCurrencyRupee size={10} />}
                          {offer.discountType === "percentage"
                            ? `${offer.discountValue}%`
                            : `₹${offer.discountValue}`}
                        </span>
                        {offer.maxDiscountAmount && (
                          <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 3 }}>
                            max ₹{offer.maxDiscountAmount}
                          </div>
                        )}
                      </td>

                      {/* Min Fare */}
                      <td style={S.td}>
                        {offer.minFareAmount > 0 ? `₹${offer.minFareAmount}` : <span style={{ color: "#9CA3AF" }}>—</span>}
                      </td>

                      {/* Valid Until */}
                      <td style={S.td}>
                        <div style={{ color: expired ? "#DC2626" : "#374151", fontWeight: expired ? 600 : 400 }}>
                          {formatDate(offer.validUntil)}
                        </div>
                        {expired && <div style={{ fontSize: 10, color: "#DC2626" }}>Expired</div>}
                      </td>

                      {/* Usage */}
                      <td style={S.td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <FiUsers size={12} color="#9CA3AF" />
                          <span>{offer.currentUsageCount || 0}</span>
                          {offer.totalUsageLimit && (
                            <span style={{ color: "#9CA3AF" }}>/ {offer.totalUsageLimit}</span>
                          )}
                        </div>
                        <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 2 }}>
                          {offer.perPassengerLimit}x per passenger
                        </div>
                      </td>

                      {/* Status */}
                      <td style={S.td}>
                        <span style={S.badge(String(offer.isActive))}>
                          {offer.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>

                      {/* Actions */}
                      <td style={S.td}>
                        <div style={{ display: "flex", gap: 4 }}>
                          {/* Preview */}
                          <button style={{ ...S.btnIcon, color: "#2563EB" }} title="View details"
                            onClick={() => setPreviewOffer(offer)}>
                            <FiEye size={15} />
                          </button>
                          {/* Edit */}
                          <button style={{ ...S.btnIcon, color: "#D97706" }} title="Edit"
                            onClick={() => openEdit(offer)}>
                            <FiEdit2 size={15} />
                          </button>
                          {/* Toggle */}
                          <button
                            style={{ ...S.btnIcon, color: offer.isActive ? "#DC2626" : "#1d9e75" }}
                            title={offer.isActive ? "Deactivate" : "Activate"}
                            onClick={() => handleToggle(offer)}>
                            {offer.isActive ? <FiToggleRight size={18} /> : <FiToggleLeft size={18} />}
                          </button>
                          {/* Delete */}
                          <button style={{ ...S.btnDanger }} title="Delete"
                            onClick={() => setDeleteConfirm(offer)}>
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Pagination ── */}
        {pagination.totalPages > 1 && (
          <div style={S.pagination}>
            <button style={S.pageBtn(false)} onClick={() => handlePageChange(currentPage - 1)} disabled={!pagination.hasPrevPage}>
              <FiChevronLeft size={16} />
            </button>
            {pageNumbers().map((p, i) =>
              p === "..." ? (
                <span key={`ellipsis-${i}`} style={{ fontSize: 13, color: "#9CA3AF", padding: "0 4px" }}>…</span>
              ) : (
                <button key={p} style={S.pageBtn(p === currentPage)} onClick={() => handlePageChange(p)}>
                  {p}
                </button>
              )
            )}
            <button style={S.pageBtn(false)} onClick={() => handlePageChange(currentPage + 1)} disabled={!pagination.hasNextPage}>
              <FiChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          CREATE / EDIT FORM MODAL
      ══════════════════════════════════════════════════════════════════════ */}
      {showForm && (
        <div style={S.overlay} onClick={() => { setShowForm(false); setFormErrors({}); }}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div style={S.modalTitle}>
                {editMode ? <FiEdit2 size={17} color="#1d9e75" /> : <FiPlus size={17} color="#1d9e75" />}
                {editMode ? "Edit Offer" : "Create New Offer"}
              </div>
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF" }}
                onClick={() => { setShowForm(false); setFormErrors({}); }}>
                <FiX size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>

              {/* Row 1: Code + Title */}
              <div style={{ ...S.row, marginBottom: 14 }}>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Offer Code *</label>
                  <input className="offer-input" style={{ ...S.input, borderColor: formErrors.code ? "#EF4444" : "#D1D5DB", textTransform: "uppercase" }}
                    placeholder="eg. MOOV20, FAST50"
                    value={form.code}
                    onChange={e => { setForm({ ...form, code: e.target.value.toUpperCase() }); setFormErrors({ ...formErrors, code: "" }); }} />
                  {formErrors.code && <span style={{ fontSize: 11, color: "#EF4444" }}>{formErrors.code}</span>}
                </div>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Title *</label>
                  <input className="offer-input" style={{ ...S.input, borderColor: formErrors.title ? "#EF4444" : "#D1D5DB" }}
                    placeholder="eg. Launch Offer"
                    value={form.title}
                    onChange={e => { setForm({ ...form, title: e.target.value }); setFormErrors({ ...formErrors, title: "" }); }} />
                  {formErrors.title && <span style={{ fontSize: 11, color: "#EF4444" }}>{formErrors.title}</span>}
                </div>
              </div>

              {/* Description */}
              <div style={{ ...S.fieldGroup, marginBottom: 14 }}>
                <label style={S.label}>Description (optional)</label>
                <textarea className="offer-input" style={{ ...S.textarea, width: "100%" }}
                  rows={2} placeholder="Short description shown to passenger"
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>

              <div style={S.divider} />

              {/* Row 2: Discount type + value + max cap */}
              <div style={{ ...S.row, marginBottom: 14 }}>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Discount Type *</label>
                  <select style={S.select} value={form.discountType}
                    onChange={e => setForm({ ...form, discountType: e.target.value, discountValue: "", maxDiscountAmount: "" })}>
                    {DISCOUNT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div style={S.fieldGroup}>
                  <label style={S.label}>{form.discountType === "percentage" ? "Discount %" : "Flat Amount (₹)"} *</label>
                  <input className="offer-input" type="number" min="1" max={form.discountType === "percentage" ? "100" : undefined}
                    style={{ ...S.input, borderColor: formErrors.discountValue ? "#EF4444" : "#D1D5DB" }}
                    placeholder={form.discountType === "percentage" ? "eg. 20" : "eg. 30"}
                    value={form.discountValue}
                    onChange={e => { setForm({ ...form, discountValue: e.target.value }); setFormErrors({ ...formErrors, discountValue: "" }); }} />
                  {formErrors.discountValue && <span style={{ fontSize: 11, color: "#EF4444" }}>{formErrors.discountValue}</span>}
                </div>
                {form.discountType === "percentage" && (
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Max Discount Cap (₹)</label>
                    <input className="offer-input" type="number" min="0" style={S.input}
                      placeholder="eg. 100 (optional)"
                      value={form.maxDiscountAmount}
                      onChange={e => setForm({ ...form, maxDiscountAmount: e.target.value })} />
                    <span style={S.hint}>50% off but max ₹100</span>
                  </div>
                )}
              </div>

              {/* Row 3: Min fare + Per passenger limit */}
              <div style={{ ...S.row, marginBottom: 14 }}>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Min Fare to Apply (₹)</label>
                  <input className="offer-input" type="number" min="0" style={S.input}
                    placeholder="eg. 100 (0 = no minimum)"
                    value={form.minFareAmount}
                    onChange={e => setForm({ ...form, minFareAmount: e.target.value })} />
                </div>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Per Passenger Limit</label>
                  <input className="offer-input" type="number" min="1" style={S.input}
                    placeholder="1"
                    value={form.perPassengerLimit}
                    onChange={e => setForm({ ...form, perPassengerLimit: e.target.value })} />
                  <span style={S.hint}>Times one passenger can use</span>
                </div>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Total Usage Limit</label>
                  <input className="offer-input" type="number" min="1" style={S.input}
                    placeholder="Unlimited (optional)"
                    value={form.totalUsageLimit}
                    onChange={e => setForm({ ...form, totalUsageLimit: e.target.value })} />
                </div>
              </div>

              <div style={S.divider} />

              {/* Row 4: Validity dates */}
              <div style={{ ...S.row, marginBottom: 14 }}>
                <div style={S.fieldGroup}>
                  <label style={S.label}><FiCalendar size={11} style={{ marginRight: 4 }} />Valid From</label>
                  <input className="offer-input" type="date" style={S.input}
                    value={form.validFrom}
                    onChange={e => setForm({ ...form, validFrom: e.target.value })} />
                </div>
                <div style={S.fieldGroup}>
                  <label style={S.label}><FiCalendar size={11} style={{ marginRight: 4 }} />Valid Until *</label>
                  <input className="offer-input" type="date" style={{ ...S.input, borderColor: formErrors.validUntil ? "#EF4444" : "#D1D5DB" }}
                    value={form.validUntil}
                    min={form.validFrom || new Date().toISOString().split("T")[0]}
                    onChange={e => { setForm({ ...form, validUntil: e.target.value }); setFormErrors({ ...formErrors, validUntil: "" }); }} />
                  {formErrors.validUntil && <span style={{ fontSize: 11, color: "#EF4444" }}>{formErrors.validUntil}</span>}
                </div>
              </div>

              <div style={S.divider} />

              {/* Vehicle types */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ ...S.label, marginBottom: 8, display: "block" }}>
                  Applicable Vehicle Types <span style={{ fontWeight: 400, color: "#9CA3AF" }}>(empty = all types)</span>
                </label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {VEHICLE_TYPES.map(v => (
                    <button key={v} type="button"
                      style={{
                        padding: "5px 14px",
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: "pointer",
                        border: form.applicableVehicleTypes.includes(v) ? "none" : "1px solid #E5E7EB",
                        background: form.applicableVehicleTypes.includes(v) ? "#1d9e75" : "#fff",
                        color: form.applicableVehicleTypes.includes(v) ? "#fff" : "#6B7280",
                        transition: "all .15s",
                        textTransform: "capitalize",
                      }}
                      onClick={() => toggleVehicle(v)}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>

              {/* Target passenger IDs */}
              <div style={{ ...S.fieldGroup, marginBottom: 14 }}>
                <label style={S.label}>Target Passenger IDs <span style={{ fontWeight: 400, color: "#9CA3AF" }}>(comma separated — empty = all passengers)</span></label>
                <input className="offer-input" style={S.input}
                  placeholder="PASS-001, PASS-002 — leave blank for all passengers"
                  value={form.targetPassengerIds}
                  onChange={e => setForm({ ...form, targetPassengerIds: e.target.value })} />
              </div>

              {/* Active toggle */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                <label style={S.label}>Status</label>
                <button type="button"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "5px 14px",
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    border: "none",
                    background: form.isActive ? "#E8F5E9" : "#FEE2E2",
                    color: form.isActive ? "#2E7D32" : "#C62828",
                    transition: "all .15s",
                  }}
                  onClick={() => setForm({ ...form, isActive: !form.isActive })}>
                  {form.isActive ? <FiToggleRight size={15} /> : <FiToggleLeft size={15} />}
                  {form.isActive ? "Active" : "Inactive"}
                </button>
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button type="button" style={S.btnGhost} className="offer-btn-ghost"
                  onClick={() => { setShowForm(false); setFormErrors({}); }}>
                  Cancel
                </button>
                <button type="submit" style={S.btnPrimary} className="offer-btn-primary" disabled={submitting}>
                  {submitting
                    ? (editMode ? "Saving…" : "Creating…")
                    : <><FiZap size={14} /> {editMode ? "Save Changes" : "Create Offer"}</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          PREVIEW MODAL
      ══════════════════════════════════════════════════════════════════════ */}
      {previewOffer && (
        <div style={S.overlay} onClick={() => setPreviewOffer(null)}>
          <div style={{ ...S.modal, maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>Offer Details</div>
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF" }}
                onClick={() => setPreviewOffer(null)}><FiX size={20} /></button>
            </div>
            {[
              ["Offer ID",       previewOffer.offerId],
              ["Code",           previewOffer.code],
              ["Title",          previewOffer.title],
              ["Description",    previewOffer.description || "—"],
              ["Discount",       previewOffer.discountType === "percentage"
                ? `${previewOffer.discountValue}%${previewOffer.maxDiscountAmount ? ` (max ₹${previewOffer.maxDiscountAmount})` : ""}`
                : `₹${previewOffer.discountValue}`],
              ["Min Fare",       previewOffer.minFareAmount > 0 ? `₹${previewOffer.minFareAmount}` : "No minimum"],
              ["Valid From",     formatDate(previewOffer.validFrom)],
              ["Valid Until",    formatDate(previewOffer.validUntil)],
              ["Total Limit",    previewOffer.totalUsageLimit ?? "Unlimited"],
              ["Per Passenger",  `${previewOffer.perPassengerLimit}x`],
              ["Used",           previewOffer.currentUsageCount || 0],
              ["Vehicle Types",  previewOffer.applicableVehicleTypes?.length > 0
                ? previewOffer.applicableVehicleTypes.join(", ")
                : "All types"],
              ["Target Passengers", previewOffer.targetPassengerIds?.length > 0
                ? previewOffer.targetPassengerIds.join(", ")
                : "All passengers"],
              ["Status",         previewOffer.isActive ? "Active" : "Inactive"],
              ["Created",        formatDate(previewOffer.createdAt)],
            ].map(([k, v]) => (
              <div key={k} style={{ display: "flex", gap: 12, padding: "8px 0", borderBottom: "1px solid #F3F4F6" }}>
                <div style={{ width: 130, fontSize: 12, fontWeight: 600, color: "#9CA3AF", flexShrink: 0 }}>{k}</div>
                <div style={{ fontSize: 13, color: "#374151", wordBreak: "break-all" }}>{String(v)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          DELETE CONFIRM MODAL
      ══════════════════════════════════════════════════════════════════════ */}
      {deleteConfirm && (
        <div style={S.overlay} onClick={() => setDeleteConfirm(null)}>
          <div style={{ ...S.modal, maxWidth: 380, textAlign: "center" }} onClick={e => e.stopPropagation()}>
            <FiTrash2 size={36} color="#DC2626" style={{ marginBottom: 12 }} />
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 8 }}>Delete Offer?</div>
            <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 6 }}>
              Are you sure you want to delete offer
            </div>
            <div style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 15, color: "#166534", background: "#F0FDF4", display: "inline-block", padding: "4px 12px", borderRadius: 6, marginBottom: 20 }}>
              {deleteConfirm.code}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button style={S.btnGhost} className="offer-btn-ghost" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button style={{ ...S.btnPrimary, background: "#DC2626" }} onClick={handleDelete}>
                <FiTrash2 size={14} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}